import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { DEFAULT_LOCALE } from 'env'
import { isEmpty, toLowerCase } from 'tools/string'
import { isObjectEmpty } from 'tools/object'

dayjs.extend(customParseFormat)
dayjs.extend(isSameOrAfter)

export const getCurrentMonth = () => new Date().getMonth()

export const getCurrentYear = () => new Date().getFullYear()

export const parseDateString = (string, format) => dayjs(string, format).toDate()

export const currentTimeInMs = () => new Date().getTime()

// OMS starting days of week is Monday

export const reactDayPickerDaysOfWeek = [ 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat' ]

export const add = (day, value, unit) => dayjs(day).add(value, unit)

export const isSameDate = (d1, d2) => dayjs(d1).isSame(dayjs(d2), 'day')

export const isAfter = (d1, d2) => dayjs(d1).isAfter(d2, 'day')

export const isToday = (day) => isSameDate(day)

export const isTodayOrAfter = ({
  day1,
  day2,
  unit = 'day',
  offsetMinute = 0
}) => dayjs(day1)
  .isSameOrAfter(
    dayjs(new Date(day2)).add(offsetMinute, 'minute'),
    unit
  )

const isTomorrow = (day) => isSameDate(dayjs(new Date()).add(1, 'day'), day)

const toJSLocale = (locale) => {
  // WORKAROUND: JS follow a different language code standard.
  if (locale === 'bm_MY') return 'ms-MY'
  return locale.replace('_', '-')
}

export const getTranslatedWeekDays = (t = x => x, weekDay = 'short') =>
  weekDay === 'short'
    ? ([ t('Sun'), t('Mon'), t('Tue'), t('Wed'), t('Thu'), t('Fri'), t('Sat') ])
    : ([ t('Sunday'), t('Monday'), t('Tuesday'), t('Wednesday'), t('Thursday'), t('Friday'), t('Saturday') ])

export const getTranslatedOMSWeekDays = (t = x => x, weekDay = 'short') => {
  return weekDay === 'short'
    ? ([ t('Mon'), t('Tue'), t('Wed'), t('Thu'), t('Fri'), t('Sat'), t('Sun') ])
    : ([ t('Monday'), t('Tuesday'), t('Wednesday'), t('Thursday'), t('Friday'), t('Saturday'), t('Sunday') ])
}

export const getTranslatedMonths = (t = x => x, month = 'long') =>
  month === 'short'
    ? ([ t('Jan'), t('Feb'), t('Mar'), t('Apr'), t('May'), t('Jun'), t('Jul'), t('Aug'), t('Sep'), t('Oct'), t('Nov'), t('Dec') ])
    : ([ t('January'), t('February'), t('March'), t('April'), t('May'), t('June'), t('July'), t('August'), t('September'), t('October'), t('November'), t('December') ])

// TODO: find a lightweight date time javascript library that support moment.calendar()
// Currently momentjs does not support modularisation which leave us no choice to bloat all their unneeded functions to our bundle
// https://github.com/moment/moment/issues/2373
// Other options like fecha, date-fns, either do not provide calendar() functionality or has static localistaion
export const formatDate = ({
  t = x => x,
  date,
  locale,
  options = { weekday: undefined, year: undefined, month: 'short', day: 'numeric' }
}) => {
  // Only support today / tomorrow
  let day = null
  if (isToday(date)) {
    day = t('Today')
  } else if (isTomorrow(date)) {
    day = t('Tomorrow')
  }

  const formattedLocale = toJSLocale(locale || DEFAULT_LOCALE)
  return day && options.weekday
    ? `${day}, ${(new Date(date)).toLocaleDateString(formattedLocale, { ...options, weekday: undefined })}`
    : (new Date(date)).toLocaleDateString(formattedLocale, options)
}

export const formatFullDate = (params) =>
  formatDate({ ...params, options: { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' } })

export const formatShortDate = (params) =>
  formatDate({ ...params, options: { weekday: 'short', year: undefined, month: 'short', day: 'numeric' } })

// OMS always sends back a range of days of week back in the following format
// { availableDays: " Mon, Tue, Wed" }
const dayStringToArrayOfDays = (daysString = '') => daysString
  .split(',')
  .map(d => getTranslatedOMSWeekDays().indexOf(d.trim()))
  .filter(d => d > -1)
  .sort()

const groupConsecutiveDays = (days) => days
  // https://stackoverflow.com/questions/47906850/javascript-group-the-numbers-from-an-array-with-series-of-consecutive-numbers
  .reduce((group, day) => {
    const lastSubArray = group[group.length - 1]
    if (!lastSubArray || lastSubArray[lastSubArray.length - 1] !== day - 1) {
      group.push([])
    }
    group[group.length - 1].push(day)
    return group
  }, [])

export const formatDayRange = (daysString, weekDay, t) => {
  const longDaysOfWeek = getTranslatedOMSWeekDays(t, weekDay)
  return groupConsecutiveDays(dayStringToArrayOfDays(daysString))
    .reduce((flatten, group) => {
      if (group.length === 1) {
        return flatten.concat(longDaysOfWeek[group[0]])
      } else if (group.length === 2) {
        return flatten.concat(group.map(d => longDaysOfWeek[d]))
      } else {
        return flatten.concat(`${longDaysOfWeek[group[0]]} - ${longDaysOfWeek[group[group.length - 1]]}`)
      }
    }, [])
    .join(', ')
}

export const formatHours = (time, format) => {
  // format only have two options 12 and 24
  if (format === 12) {
    return dayjs(time, 'HH:mm').format('hh:mm A')
  }
  return dayjs(time, 'hh:mm A').format('HH:mm')
}

export const convertTimeToMinutes = (time) => {
  const [hours, minutes] = formatHours(time, 24).split(':')
  return (Number.parseInt(hours, 10) * 60) + Number.parseInt(minutes, 10)
}

export const checkValidTimeSlot = ({ date, currentTimeSlot, availableTimeSlots }) => {
  if (isAfter(new Date(), date) || isEmpty(currentTimeSlot) || availableTimeSlots.length === 0) {
    return {
      isValidTimeSlot: false,
      availableTimeSlot: ''
    }
  }
  if (currentTimeSlot === 'EARLIEST_AVAILABLE') {
    return {
      isValidTimeSlot: true,
      availableTimeSlot: availableTimeSlots[0].y
    }
  }
  const splitCurrentTimeSlot = (currentTimeSlot).split(' to ')
  const validTimeSlot = (availableTimeSlots || []).find(timeSlot => {
    const splitTimeSlot = timeSlot.y.split(' to ')
    return toLowerCase(splitTimeSlot[0]) === toLowerCase(splitCurrentTimeSlot[0]) && toLowerCase(splitTimeSlot[1]) === toLowerCase(splitCurrentTimeSlot[1])
  })

  return {
    isValidTimeSlot: !isObjectEmpty(validTimeSlot),
    availableTimeSlot: !isObjectEmpty(validTimeSlot) ? validTimeSlot.y : ''
  }
}

export const getDay = (date) => {
  const getDay = new Date(date).getDay()
  const days = {
    0: 'Sunday',
    1: 'Monday',
    2: 'Tuesday',
    3: 'Wednesday',
    4: 'Thursday',
    5: 'Friday',
    6: 'Saturday'
  }

  return days[getDay]
}

export const getEarliestCollectionTime = ({ collectionTime, availableTimeSlots }) => {
  const [start, end] = collectionTime.split('to')
  return availableTimeSlots.find(timeSlot => toLowerCase(timeSlot.y.split('to')[0]) === toLowerCase(start)).z
}
